// src/pages/services/AMCContracts.js
// AMC Contract Master — SM / Manager / Owner
//
// TO-8 FINAL:
// - Two creation modes: "From Project" + "Manual"
// - From Project: select project → all sites auto-load → per-site amount
// - Per-site invoices (billing_type: "per_site")
// - Each site = separate amc_contract doc
// - Project marked amc_status: "converted" after conversion
// - Auto-expire on load
// - Deactivate / Reactivate
// - Due / Due Soon badges

import { useEffect, useState, useCallback } from "react";
import {
  collection, getDocs, addDoc, updateDoc,
  doc, query, where, serverTimestamp, getDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import {
  QuickCreateButton,
  QuickCreateModal,
  QuickCreateClient,
  QuickCreateSite,
} from "../../components/quickcreate";
import { fetchClientsWithSites, fetchSitesForClient, autoSelectSiteIfOnlyOne } from "../../services/clientService";

const FREQUENCIES = [
  { value: "monthly",   label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "yearly",    label: "Yearly" },
];

export function calcNextInvoiceDate(baseDateStr, frequency) {
  if (!baseDateStr) return "";
  const d = new Date(baseDateStr);
  if (isNaN(d)) return "";
  switch (frequency) {
    case "monthly":   d.setMonth(d.getMonth() + 1);        break;
    case "quarterly": d.setMonth(d.getMonth() + 3);        break;
    case "yearly":    d.setFullYear(d.getFullYear() + 1);  break;
    default: break;
  }
  return d.toISOString().split("T")[0];
}

function StatusBadge({ status }) {
  const map = {
    active:   "bg-green-100 text-green-700",
    inactive: "bg-gray-100 text-gray-600",
    expired:  "bg-red-100 text-red-700",
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${map[status] || map.inactive}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown"}
    </span>
  );
}

export default function AMCContracts() {
  const me         = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = me.company_id || "";

  const [contracts,   setContracts]   = useState([]);
  const [projects,    setProjects]    = useState([]);
  const [clients,     setClients]     = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState("all");
  const [search,      setSearch]      = useState("");   // ── SAFE ADD: search

  // ── SAFE ADD: Edit modal state ─────────────────────────────────────────
  const [showEdit,    setShowEdit]    = useState(false);
  const [editContract, setEditContract] = useState(null); // contract being edited
  const [editForm,    setEditForm]    = useState({
    amount: "", frequency: "monthly", end_date: "", description: "",
  });
  const [editSaving,  setEditSaving]  = useState(false);
  const [editError,   setEditError]   = useState("");

  const [showModal,   setShowModal]   = useState(false);
  const [createMode,  setCreateMode]  = useState("");
  const [qcOpen,      setQcOpen]      = useState(null); // "client" | "site" | null — manual form quick-create

  // From Project form
  const [projForm,    setProjForm]    = useState({
    project_id: "", project_name: "", client_id: "", client_name: "",
    frequency: "monthly", start_date: "", end_date: "", description: "",
  });
  const [projSites,   setProjSites]   = useState([]);
  const [siteAmounts, setSiteAmounts] = useState({});
  const [projLoading, setProjLoading] = useState(false);
  const [projError,   setProjError]   = useState("");
  const [projSaving,  setProjSaving]  = useState(false);

  // Manual form
  const [manForm,  setManForm]  = useState({
    client_id: "", client_name: "", site_id: "", site_name: "",
    description: "", amount: "", frequency: "monthly",
    start_date: "", end_date: "",
  });
  const [manSites,  setManSites]  = useState([]);
  const [manError,  setManError]  = useState("");
  const [manSaving, setManSaving] = useState(false);

  const loadContracts = useCallback(async () => {
    if (!company_id) return;
    try {
      setLoading(true);
      const snap = await getDocs(query(
        collection(db, "amc_contracts"),
        where("company_id", "==", company_id)
      ));
      const today = new Date(); today.setHours(0,0,0,0);
      const rows = []; const expireUpdates = [];
      snap.forEach(d => {
        const data = { id: d.id, ...d.data() };
        if (data.status === "active" && data.end_date) {
          const end = new Date(data.end_date); end.setHours(0,0,0,0);
          if (end < today) {
            data.status = "expired";
            expireUpdates.push(updateDoc(doc(db, "amc_contracts", d.id), {
              status: "expired", updated_at: serverTimestamp(),
            }));
          }
        }
        rows.push(data);
      });
      if (expireUpdates.length) Promise.all(expireUpdates).catch(e => console.warn("[AMC] expire:", e));
      rows.sort((a, b) => (b.created_at?.seconds || 0) - (a.created_at?.seconds || 0));
      setContracts(rows);
    } catch (err) { console.error("[AMC] loadContracts:", err); }
    finally { setLoading(false); }
  }, [company_id]);

  const loadProjects = useCallback(async () => {
    if (!company_id) return;
    try {
      const snap = await getDocs(query(
        collection(db, "projects"), where("company_id", "==", company_id)
      ));
      setProjects(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(p => p.amc_status !== "converted"));
    } catch (err) { console.error("[AMC] loadProjects:", err); }
  }, [company_id]);

  // Only clients that have at least one site (in the sites SOT collection)
  // are shown — prevents assigning an AMC contract to a client with no site.
  const loadClients = useCallback(async () => {
    if (!company_id) return;
    try {
      setClients(await fetchClientsWithSites(company_id));
    } catch (err) { console.error("[AMC] loadClients:", err); }
  }, [company_id]);

  useEffect(() => {
    loadContracts(); loadProjects(); loadClients();
  }, [loadContracts, loadProjects, loadClients]);

  const handleProjectSelect = async (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    let clientName = "";
    try {
      const cSnap = await getDoc(doc(db, "clients", project.client_id));
      if (cSnap.exists()) clientName = cSnap.data().client_name || "";
    } catch {}
    setProjForm(prev => ({
      ...prev, project_id: project.id,
      project_name: project.project_name || "",
      client_id: project.client_id || "", client_name: clientName,
    }));
    setProjError("");
    setProjLoading(true);
    try {
      // FIX: sites docs never carry a project_id field — only client_id
      // (D-154 schema). The old query filtered by project_id and always
      // returned empty, showing "No sites found" even when the client
      // genuinely had sites. Query by the project's own client_id instead,
      // same as every other site-lookup in the app (clientService.js).
      const snap = await getDocs(query(
        collection(db, "sites"),
        where("company_id", "==", company_id),
        where("client_id", "==", project.client_id)
      ));
      const siteList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setProjSites(siteList);
      const amounts = {};
      siteList.forEach(s => { amounts[s.id] = ""; });
      setSiteAmounts(amounts);
      if (siteList.length === 0) setProjError("No sites found under this project. Add sites first.");
    } catch (err) { setProjError("Failed to load sites"); }
    finally { setProjLoading(false); }
  };

  const handleSaveFromProject = async () => {
    if (!projForm.project_id)   { setProjError("Select a project"); return; }
    if (projSites.length === 0) { setProjError("No sites to convert"); return; }
    if (!projForm.start_date)   { setProjError("Start date required"); return; }
    if (!projForm.end_date)     { setProjError("End date required"); return; }
    if (new Date(projForm.end_date) <= new Date(projForm.start_date)) {
      setProjError("End date must be after start date"); return;
    }
    for (const site of projSites) {
      if (!Number(siteAmounts[site.id] || 0)) {
        setProjError(`Amount required for: ${site.site_name}`); return;
      }
    }
    try {
      setProjSaving(true); setProjError("");
      const snap = await getDocs(query(collection(db, "amc_contracts"), where("company_id", "==", company_id)));
      let contractCount = snap.size;
      const contractIds = [];
      for (const site of projSites) {
        contractCount++;
        const contract_no = `AMC-${String(contractCount).padStart(3, "0")}`;
        const ref = await addDoc(collection(db, "amc_contracts"), {
          company_id, contract_no,
          billing_type: "per_site", created_from_project: true,
          project_id: projForm.project_id, project_name: projForm.project_name,
          client_id: projForm.client_id, client_name: projForm.client_name,
          site_id: site.id, site_name: site.site_name || "",
          site_address: site.address || "",
          description: projForm.description.trim() ||
            `AMC — ${projForm.project_name} — ${site.site_name}`,
          amount: Number(siteAmounts[site.id]),
          frequency: projForm.frequency,
          start_date: projForm.start_date, end_date: projForm.end_date,
          next_invoice_date: projForm.start_date,
          status: "active", last_invoice_id: "", last_invoice_date: "",
          created_by: me.uid || me.id || "", created_by_name: me.name || "",
          created_at: serverTimestamp(), updated_at: serverTimestamp(),
        });
        contractIds.push(ref.id);
      }
      await updateDoc(doc(db, "projects", projForm.project_id), {
        amc_status: "converted", amc_contract_ids: contractIds,
        amc_converted_at: serverTimestamp(),
        amc_converted_by: me.uid || me.id || "",
      });
      setProjForm({ project_id: "", project_name: "", client_id: "", client_name: "",
        frequency: "monthly", start_date: "", end_date: "", description: "" });
      setProjSites([]); setSiteAmounts({});
      setShowModal(false); setCreateMode("");
      await loadContracts(); await loadProjects();
    } catch (err) {
      console.error("[AMC] saveFromProject:", err);
      setProjError("Failed to save. Please try again.");
    } finally { setProjSaving(false); }
  };

  // Auto-selects the site if the client has exactly one — saves the operator
  // a step for the common single-site case.
  const handleManClientChange = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setManForm(prev => ({ ...prev, client_id: clientId,
      client_name: client?.client_name || "", site_id: "", site_name: "" }));
    if (!clientId) { setManSites([]); return; }
    try {
      const siteList = await fetchSitesForClient(company_id, clientId);
      setManSites(siteList);

      const onlySite = autoSelectSiteIfOnlyOne(siteList);
      if (onlySite) {
        setManForm(prev => ({ ...prev, site_id: onlySite.id, site_name: onlySite.site_name }));
      }
    } catch (err) { console.error("[AMC] manSites:", err); }
  };

  const handleSaveManual = async () => {
    if (!manForm.client_id) { setManError("Client required"); return; }
    if (!manForm.site_id) { setManError("Site is required"); return; }
    if (!manForm.amount || Number(manForm.amount) <= 0) { setManError("Amount must be > 0"); return; }
    if (!manForm.start_date) { setManError("Start date required"); return; }
    if (!manForm.end_date)   { setManError("End date required"); return; }
    if (new Date(manForm.end_date) <= new Date(manForm.start_date)) {
      setManError("End date must be after start date"); return;
    }
    try {
      setManSaving(true); setManError("");
      const snap = await getDocs(query(collection(db, "amc_contracts"), where("company_id", "==", company_id)));
      const contract_no = `AMC-${String(snap.size + 1).padStart(3, "0")}`;
      await addDoc(collection(db, "amc_contracts"), {
        company_id, contract_no,
        billing_type: "per_site", created_from_project: false,
        project_id: "", project_name: "",
        client_id: manForm.client_id, client_name: manForm.client_name,
        site_id: manForm.site_id || "", site_name: manForm.site_name || "",
        description: manForm.description.trim() ||
          `AMC — ${manForm.client_name}${manForm.site_name ? ` — ${manForm.site_name}` : ""}`,
        amount: Number(manForm.amount), frequency: manForm.frequency,
        start_date: manForm.start_date, end_date: manForm.end_date,
        next_invoice_date: manForm.start_date,
        status: "active", last_invoice_id: "", last_invoice_date: "",
        created_by: me.uid || me.id || "", created_by_name: me.name || "",
        created_at: serverTimestamp(), updated_at: serverTimestamp(),
      });
      setManForm({ client_id: "", client_name: "", site_id: "", site_name: "",
        description: "", amount: "", frequency: "monthly", start_date: "", end_date: "" });
      setManSites([]); setShowModal(false); setCreateMode("");
      await loadContracts();
    } catch (err) {
      console.error("[AMC] saveManual:", err); setManError("Failed to save.");
    } finally { setManSaving(false); }
  };

  // ── SAFE ADD: Save edit ───────────────────────────────────────────────
  const handleOpenEdit = (contract) => {
    setEditContract(contract);
    setEditForm({
      amount:      String(contract.amount || ""),
      frequency:   contract.frequency    || "monthly",
      end_date:    contract.end_date     || "",
      description: contract.description || "",
    });
    setEditError("");
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!editContract) return;
    if (!editForm.amount || Number(editForm.amount) <= 0) {
      setEditError("Amount must be > 0"); return;
    }
    if (!editForm.end_date) { setEditError("End date required"); return; }
    if (editContract.start_date && new Date(editForm.end_date) <= new Date(editContract.start_date)) {
      setEditError("End date must be after start date"); return;
    }
    try {
      setEditSaving(true); setEditError("");

      // ── Frequency changed → recalculate next_invoice_date ────────────
      // Base: last_invoice_date (if invoiced before) else start_date
      // Prevents stale schedule when billing cycle changes
      let next_invoice_date = editContract.next_invoice_date || editContract.start_date;
      if (editForm.frequency !== editContract.frequency) {
        const base = editContract.last_invoice_date || editContract.start_date || "";
        next_invoice_date = calcNextInvoiceDate(base, editForm.frequency);
        console.log("[AMC] frequency changed →", editContract.frequency, "→", editForm.frequency,
          "| next_invoice_date recalculated:", next_invoice_date);
      }

      await updateDoc(doc(db, "amc_contracts", editContract.id), {
        amount:             Number(editForm.amount),
        frequency:          editForm.frequency,
        end_date:           editForm.end_date,
        description:        editForm.description.trim(),
        next_invoice_date,  // ← recalculated if frequency changed, else unchanged
        updated_at:         serverTimestamp(),
        updated_by:         me.uid || me.id || "",
      });
      setShowEdit(false);
      setEditContract(null);
      await loadContracts();
    } catch (err) {
      console.error("[AMC] saveEdit:", err);
      setEditError("Failed to save. Try again.");
    } finally { setEditSaving(false); }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this AMC contract?")) return;
    try {
      await updateDoc(doc(db, "amc_contracts", id), { status: "inactive", updated_at: serverTimestamp() });
      await loadContracts();
    } catch { alert("Failed to deactivate"); }
  };

  const handleReactivate = async (id) => {
    try {
      await updateDoc(doc(db, "amc_contracts", id), { status: "active", updated_at: serverTimestamp() });
      await loadContracts();
    } catch { alert("Failed to reactivate"); }
  };

  // ── SAFE ADD: search filter ───────────────────────────────────────────
  const filtered = contracts.filter(c => {
    const matchStatus = filter === "all" ? true : c.status === filter;
    const q = search.toLowerCase().trim();
    const matchSearch = !q ||
      c.contract_no?.toLowerCase().includes(q) ||
      c.client_name?.toLowerCase().includes(q) ||
      c.site_name?.toLowerCase().includes(q) ||
      c.project_name?.toLowerCase().includes(q);
    return matchStatus && matchSearch;
  });
  const counts = {
    all: contracts.length,
    active: contracts.filter(c => c.status === "active").length,
    expired: contracts.filter(c => c.status === "expired").length,
    inactive: contracts.filter(c => c.status === "inactive").length,
  };
  const totalActive = contracts.filter(c => c.status === "active")
    .reduce((s, c) => s + Number(c.amount || 0), 0);

  const today = new Date(); today.setHours(0,0,0,0);
  const in7 = new Date(today.getTime() + 7*24*60*60*1000);
  const isDue     = c => c.status === "active" && c.next_invoice_date && new Date(c.next_invoice_date) <= today;
  const isDueSoon = c => c.status === "active" && c.next_invoice_date &&
    new Date(c.next_invoice_date) > today && new Date(c.next_invoice_date) <= in7;

  const closeModal = () => {
    setShowModal(false); setCreateMode(""); setQcOpen(null);
    setProjError(""); setManError("");
    setProjSites([]); setSiteAmounts({});
  };

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-6xl mx-auto">

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">AMC Contracts</h1>
          <p className="text-sm text-slate-500 mt-0.5">Annual Maintenance Contract management</p>
        </div>
        <button onClick={() => { setShowModal(true); setCreateMode(""); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold hover:bg-blue-700 transition">
          + New Contract
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { key: "all",      label: "Total",         color: "bg-slate-50 border-slate-200",   val: counts.all },
          { key: "active",   label: "Active",        color: "bg-green-50 border-green-200",   val: counts.active },
          { key: "expired",  label: "Expired",       color: "bg-red-50 border-red-200",       val: counts.expired },
          { key: "inactive", label: "Inactive",      color: "bg-gray-50 border-gray-200",     val: counts.inactive },
          { key: null,       label: "Monthly Value", color: "bg-purple-50 border-purple-200", val: `₹${totalActive.toLocaleString("en-IN")}` },
        ].map(({ key, label, color, val }) => (
          <button key={label} onClick={() => key && setFilter(key)}
            className={`rounded-xl border p-3 text-left transition ${color} ${filter === key ? "ring-2 ring-blue-400" : ""} ${!key ? "cursor-default" : ""}`}>
            <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
            <p className="text-xl font-bold mt-1">{val}</p>
          </button>
        ))}
      </div>

      {/* SAFE ADD: Search bar */}
      <div className="flex items-center gap-3">
        <input
          type="text"
          placeholder="Search by contract no, client, site, project..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300"
        />
        {search && (
          <button onClick={() => setSearch("")}
            className="px-3 py-3 text-slate-400 hover:text-slate-600 text-sm">
            ✕ Clear
          </button>
        )}
      </div>

      {/* List */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        {loading ? (
          <p className="p-6 text-gray-400 text-sm">Loading contracts...</p>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-gray-400 text-sm mb-1">No contracts found.</p>
            <button onClick={() => setShowModal(true)} className="text-blue-600 text-sm hover:underline">
              + Create your first AMC contract
            </button>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {filtered.map(c => (
              <div key={c.id}
                className={`p-4 hover:bg-slate-50 transition ${isDue(c) ? "bg-yellow-50 hover:bg-yellow-50" : ""}`}>

                {/* Row 1 — Contract No + Status + Badges */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-slate-800 text-sm">{c.contract_no}</span>
                    {isDue(c) && (
                      <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-semibold">Due</span>
                    )}
                    {isDueSoon(c) && (
                      <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-semibold">Soon</span>
                    )}
                    {c.project_name && (
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">
                        {c.project_name}
                      </span>
                    )}
                    {!c.project_name && (
                      <span className="text-xs bg-gray-100 text-gray-400 px-2 py-0.5 rounded-full">Manual</span>
                    )}
                  </div>
                  <StatusBadge status={c.status} />
                </div>

                {/* Row 2 — Client + Site + Frequency + Amount */}
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mb-2">
                  <span className="text-slate-700 font-medium">{c.client_name}</span>
                  {c.site_name && (
                    <span className="text-slate-500">📍 {c.site_name}</span>
                  )}
                  <span className="text-slate-500 capitalize">🔄 {c.frequency}</span>
                  <span className="text-green-700 font-semibold">
                    ₹{Number(c.amount || 0).toLocaleString("en-IN")}
                  </span>
                </div>

                {/* Row 3 — Period + Next Invoice + Actions */}
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                    <span>📅 {c.start_date} → {c.end_date}</span>
                    <span className={`font-medium ${
                      isDue(c) ? "text-red-600" : isDueSoon(c) ? "text-blue-600" : "text-slate-500"
                    }`}>
                      Next Invoice: {c.next_invoice_date || "—"}
                    </span>
                    {c.last_invoice_date && (
                      <span className="text-slate-400">Last: {c.last_invoice_date}</span>
                    )}
                  </div>
                  <div className="flex gap-3">
                    {(c.status === "active" || c.status === "inactive") && (
                      <button onClick={() => handleOpenEdit(c)}
                        className="text-xs text-blue-500 hover:text-blue-700 font-semibold">
                        Edit
                      </button>
                    )}
                    {c.status === "active" && (
                      <button onClick={() => handleDeactivate(c.id)}
                        className="text-xs text-red-500 hover:text-red-700 font-semibold">
                        Deactivate
                      </button>
                    )}
                    {c.status === "inactive" && (
                      <button onClick={() => handleReactivate(c.id)}
                        className="text-xs text-green-600 hover:text-green-800 font-semibold">
                        Reactivate
                      </button>
                    )}
                    {c.status === "expired" && (
                      <span className="text-xs text-gray-400">Expired</span>
                    )}
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto">

            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold text-slate-800">
                {createMode === "project" ? "Convert Project to AMC" :
                 createMode === "manual"  ? "New AMC Contract (Manual)" :
                 "New AMC Contract"}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            {/* Mode select */}
            {!createMode && (
              <div className="px-6 py-8 space-y-4">
                <p className="text-sm text-slate-500 mb-2">Choose creation method:</p>

                <button onClick={() => setCreateMode("project")}
                  className="w-full p-4 border-2 border-blue-200 bg-blue-50 rounded-2xl text-left hover:border-blue-400 transition">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center text-xl shrink-0">🏗️</div>
                    <div>
                      <p className="font-semibold text-blue-800 text-sm">From Project <span className="ml-1 text-xs bg-blue-200 text-blue-700 px-1.5 py-0.5 rounded-full">Recommended</span></p>
                      <p className="text-xs text-blue-600 mt-1">
                        Convert a completed project to AMC. All project sites auto-load with per-site billing.
                      </p>
                      {projects.length === 0 && (
                        <p className="text-xs text-orange-500 mt-1">⚠ No eligible projects (all converted already)</p>
                      )}
                    </div>
                  </div>
                </button>

                <button onClick={() => setCreateMode("manual")}
                  className="w-full p-4 border-2 border-slate-200 bg-slate-50 rounded-2xl text-left hover:border-slate-400 transition">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-xl shrink-0">✍️</div>
                    <div>
                      <p className="font-semibold text-slate-700 text-sm">Manual Entry</p>
                      <p className="text-xs text-slate-500 mt-1">Create contract manually for a specific client and site.</p>
                    </div>
                  </div>
                </button>
              </div>
            )}

            {/* From Project Form */}
            {createMode === "project" && (
              <div className="px-6 py-5 space-y-4">
                {projError && (
                  <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{projError}</div>
                )}

                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1">Project *</label>
                  <select value={projForm.project_id} onChange={e => handleProjectSelect(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white">
                    <option value="">Select Project</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.project_name}</option>)}
                  </select>
                </div>

                {projForm.client_name && (
                  <div className="px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-sm">
                    <span className="text-slate-400 text-xs">Client: </span>
                    <span className="font-semibold text-slate-700">{projForm.client_name}</span>
                  </div>
                )}

                {projLoading && <p className="text-xs text-blue-500">Loading project sites...</p>}

                {projSites.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-2">
                      Per-Site Amount ₹ * <span className="font-normal text-slate-400">({projSites.length} sites)</span>
                    </label>
                    <div className="space-y-2">
                      {projSites.map(site => (
                        <div key={site.id} className="flex items-center gap-3 border border-slate-200 rounded-xl p-3">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-slate-700">{site.site_name}</p>
                            {site.address && <p className="text-xs text-slate-400 mt-0.5">{site.address}</p>}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className="text-slate-400 text-sm">₹</span>
                            <input type="number" min="1" placeholder="Amount"
                              value={siteAmounts[site.id] || ""}
                              onChange={e => setSiteAmounts(prev => ({ ...prev, [site.id]: e.target.value }))}
                              className="w-28 border border-slate-200 rounded-lg p-2 text-sm outline-none focus:ring-2 focus:ring-blue-300 text-right" />
                          </div>
                        </div>
                      ))}
                    </div>
                    {Object.values(siteAmounts).some(a => Number(a) > 0) && (
                      <div className="mt-2 px-4 py-2 bg-green-50 border border-green-200 rounded-xl flex justify-between text-sm">
                        <span className="text-green-700 font-semibold">Total per cycle</span>
                        <span className="text-green-800 font-bold">
                          ₹{Object.values(siteAmounts).reduce((s, a) => s + Number(a||0), 0).toLocaleString("en-IN")}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1">Description <span className="font-normal text-slate-400">(optional)</span></label>
                  <input type="text" placeholder="e.g. Annual HVAC Preventive Maintenance"
                    value={projForm.description}
                    onChange={e => setProjForm(p => ({ ...p, description: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Frequency *</label>
                    <select value={projForm.frequency} onChange={e => setProjForm(p => ({ ...p, frequency: e.target.value }))}
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white">
                      {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Start Date *</label>
                    <input type="date" value={projForm.start_date}
                      onChange={e => setProjForm(p => ({ ...p, start_date: e.target.value }))}
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">End Date *</label>
                    <input type="date" value={projForm.end_date} min={projForm.start_date || ""}
                      onChange={e => setProjForm(p => ({ ...p, end_date: e.target.value }))}
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                  </div>
                </div>

                {projForm.start_date && projSites.length > 0 && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700 space-y-1">
                    <p className="font-semibold">What will be created:</p>
                    <p>• <strong>{projSites.length} contracts</strong> (one per site)</p>
                    <p>• First invoice date: <strong>{projForm.start_date}</strong></p>
                    <p>• Next after that: <strong>{calcNextInvoiceDate(projForm.start_date, projForm.frequency)}</strong></p>
                    <p>• Project marked as <strong>Converted to AMC ✓</strong></p>
                  </div>
                )}

                <div className="flex justify-between pt-2">
                  <button onClick={() => setCreateMode("")} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">← Back</button>
                  <button onClick={handleSaveFromProject} disabled={projSaving || projSites.length === 0}
                    className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition">
                    {projSaving ? "Creating..." : `Convert → ${projSites.length} Contract${projSites.length !== 1 ? "s" : ""}`}
                  </button>
                </div>
              </div>
            )}

            {/* Manual Form */}
            {createMode === "manual" && (
              <div className="px-6 py-5 space-y-4">
                {manError && (
                  <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{manError}</div>
                )}

                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1">Client *</label>
                  <div className="flex gap-2 items-center">
                    <select value={manForm.client_id} onChange={e => handleManClientChange(e.target.value)}
                      className="flex-1 border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white">
                      <option value="">Select Client</option>
                      {clients.map(c => <option key={c.id} value={c.id}>{c.client_name}</option>)}
                    </select>
                    <QuickCreateButton onClick={() => setQcOpen("client")} label="Add client" />
                  </div>
                </div>

                {manForm.client_id && (
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Site *</label>
                    <div className="flex gap-2 items-center">
                      <select value={manForm.site_id}
                        onChange={e => {
                          const s = manSites.find(s => s.id === e.target.value);
                          setManForm(p => ({ ...p, site_id: e.target.value, site_name: s?.site_name || "" }));
                        }}
                        className="flex-1 border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white">
                        <option value="">Select Site</option>
                        {manSites.map(s => <option key={s.id} value={s.id}>{s.site_name}</option>)}
                      </select>
                      <QuickCreateButton onClick={() => setQcOpen("site")} label="Add site" />
                    </div>
                    {manSites.length === 1 && manForm.site_id === manSites[0].id && (
                      <p className="text-xs text-green-600 mt-2 font-medium">
                        ✓ Auto-selected (only site for this client)
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-slate-500 block mb-1">Description <span className="font-normal text-slate-400">(optional)</span></label>
                  <input type="text" placeholder="e.g. Annual HVAC Maintenance"
                    value={manForm.description} onChange={e => setManForm(p => ({ ...p, description: e.target.value }))}
                    className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Amount ₹ *</label>
                    <input type="number" min="1" placeholder="Per cycle"
                      value={manForm.amount} onChange={e => setManForm(p => ({ ...p, amount: e.target.value }))}
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Frequency *</label>
                    <select value={manForm.frequency} onChange={e => setManForm(p => ({ ...p, frequency: e.target.value }))}
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white">
                      {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">Start Date *</label>
                    <input type="date" value={manForm.start_date}
                      onChange={e => setManForm(p => ({ ...p, start_date: e.target.value }))}
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-500 block mb-1">End Date *</label>
                    <input type="date" value={manForm.end_date} min={manForm.start_date || ""}
                      onChange={e => setManForm(p => ({ ...p, end_date: e.target.value }))}
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
                  </div>
                </div>

                {manForm.start_date && manForm.amount && Number(manForm.amount) > 0 && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 text-xs text-blue-700 space-y-1">
                    <p className="font-semibold">Contract Preview</p>
                    <p>First invoice: <strong>{manForm.start_date}</strong></p>
                    <p>Next after that: <strong>{calcNextInvoiceDate(manForm.start_date, manForm.frequency)}</strong></p>
                    <p>Amount per cycle: <strong>₹{Number(manForm.amount).toLocaleString("en-IN")}</strong></p>
                  </div>
                )}

                <div className="flex justify-between pt-2">
                  <button onClick={() => setCreateMode("")} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">← Back</button>
                  <button onClick={handleSaveManual} disabled={manSaving}
                    className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition">
                    {manSaving ? "Saving..." : "Create Contract"}
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      )}
      {/* ── SAFE ADD: Edit Modal ─────────────────────────────────────────── */}
      {showEdit && editContract && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">

            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold text-slate-800">Edit Contract</h2>
                <p className="text-sm text-slate-500">{editContract.contract_no} — {editContract.site_name || editContract.client_name}</p>
              </div>
              <button onClick={() => setShowEdit(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            <div className="px-6 py-5 space-y-4">

              {editError && (
                <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{editError}</div>
              )}

              {/* Read-only info */}
              <div className="grid grid-cols-2 gap-3 px-4 py-3 bg-slate-50 rounded-xl text-sm">
                <div>
                  <p className="text-xs text-slate-400">Client</p>
                  <p className="font-medium text-slate-700">{editContract.client_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Site</p>
                  <p className="font-medium text-slate-700">{editContract.site_name || "—"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Start Date</p>
                  <p className="font-medium text-slate-700">{editContract.start_date}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Contract No</p>
                  <p className="font-medium text-slate-700">{editContract.contract_no}</p>
                </div>
              </div>

              {/* Editable fields */}
              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">Amount ₹ *</label>
                <input type="number" min="1"
                  value={editForm.amount}
                  onChange={e => setEditForm(p => ({ ...p, amount: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">Frequency *</label>
                <select value={editForm.frequency}
                  onChange={e => setEditForm(p => ({ ...p, frequency: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300 bg-white">
                  {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">End Date *</label>
                <input type="date" value={editForm.end_date}
                  min={editContract.start_date || ""}
                  onChange={e => setEditForm(p => ({ ...p, end_date: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-500 block mb-1">
                  Description <span className="font-normal text-slate-400">(optional)</span>
                </label>
                <input type="text"
                  value={editForm.description}
                  onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl p-3 text-sm outline-none focus:ring-2 focus:ring-blue-300" />
              </div>

            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-between">
              <button onClick={() => setShowEdit(false)}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                Cancel
              </button>
              <button onClick={handleSaveEdit} disabled={editSaving}
                className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition">
                {editSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>

          </div>
       </div>
      )}

      {/* QUICK CREATE — Client (Manual AMC form) */}
      <QuickCreateModal
        open={qcOpen === "client"}
        onClose={() => setQcOpen(null)}
        title="Quick Create — Client"
      >
        <QuickCreateClient
          companyId={company_id}
          onCreated={(newClient) => {
            setClients(prev => [...prev, newClient]);
            setManForm(p => ({
              ...p,
              client_id:   newClient.id,
              client_name: newClient.client_name,
              site_id: "", site_name: "",
            }));
            setManSites([]); // naye client ki abhi koi site nahi
            setQcOpen(null);
          }}
          onCancel={() => setQcOpen(null)}
        />
      </QuickCreateModal>

      {/* QUICK CREATE — Site (Manual AMC form) */}
      <QuickCreateModal
        open={qcOpen === "site"}
        onClose={() => setQcOpen(null)}
        title="Quick Create — Site"
      >
        <QuickCreateSite
          companyId={company_id}
          clientId={manForm.client_id}
          clientName={manForm.client_name}
          onCreated={(newSite) => {
            setManSites(prev => [...prev, newSite]);
            setManForm(p => ({
              ...p,
              site_id:   newSite.id,
              site_name: newSite.site_name,
            }));
            setQcOpen(null);
          }}
          onCancel={() => setQcOpen(null)}
        />
      </QuickCreateModal>

    </div>
  );
}